import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "@/lib/api";


export default function TransactionsPage() {
  const { authHeaders, isAdmin } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [clientFilter, setClientFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [storeSearch, setStoreSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = (p = page, cf = clientFilter, tf = typeFilter, ss = storeSearch) => {
    let url = `${API}/transactions?page=${p}&limit=20`;
    if (cf && cf !== "all") url += `&client_id=${cf}`;
    if (tf && tf !== "all") url += `&tx_type=${tf}`;
    if (ss) url += `&store_id=${encodeURIComponent(ss)}`;
    setLoading(true);
    axios.get(url, { headers: authHeaders() }).then((r) => {
      setTransactions(r.data.transactions || []);
      setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/vendors`, { headers: authHeaders() }).then((r) => setVendors(r.data)).catch(() => {});
    }
    load();
  }, []);

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div>
        <h1 className="text-2xl font-bold text-ink tracking-tight">Transaction Audit Log</h1>
        <p className="text-sm text-ink/50 mt-0.5">{total.toLocaleString()} total transactions</p>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Input
          placeholder="Filter by store ID…"
          value={storeSearch}
          onChange={(e) => setStoreSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(1, clientFilter, typeFilter, storeSearch); } }}
          className="h-9 text-sm w-48"
          data-testid="store-search-input"
        />
        {isAdmin && (
          <Select value={clientFilter} onValueChange={(v) => { setClientFilter(v); setPage(1); load(1, v, typeFilter, storeSearch); }}>
            <SelectTrigger className="w-44 h-9 text-sm" data-testid="vendor-filter">
              <SelectValue placeholder="All Vendors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {vendors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); load(1, clientFilter, v, storeSearch); }}>
          <SelectTrigger className="w-36 h-9 text-sm" data-testid="type-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="earn">Earn</SelectItem>
            <SelectItem value="redeem">Redeem</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => { setPage(1); load(1, clientFilter, typeFilter, storeSearch); }} className="h-9 text-sm" data-testid="apply-filters-button">
          Apply
        </Button>
      </div>

      <div className="bg-white rounded-md border border-ink/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface">
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Tx ID</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Vendor</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Customer</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Store</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Amount</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Points</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Type</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Timestamp</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-ink/40 text-sm">Loading…</TableCell></TableRow>
            ) : transactions.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-ink/40 text-sm">No transactions found</TableCell></TableRow>
            ) : (
              transactions.map((t) => (
                <TableRow key={t.id} data-testid={`tx-row-${t.id}`}>
                  <TableCell className="font-mono text-xs text-ink/40">{t.tx_id?.slice(0, 8)}…</TableCell>
                  <TableCell className="text-sm text-ink/80">{t.vendor_name || "—"}</TableCell>
                  <TableCell>
                    <p className="text-sm">{t.customer_name || "Unknown"}</p>
                    <p className="text-xs text-ink/40">{t.customer_email || "—"}</p>
                  </TableCell>
                  <TableCell className="text-sm font-mono">{t.store_id}</TableCell>
                  <TableCell className="font-mono text-sm">£{t.total_amount_gbp?.toFixed(2)}</TableCell>
                  <TableCell className={`font-mono text-sm font-semibold ${t.type === "earn" ? "text-ink" : "text-ink/40"}`}>
                    {t.type === "earn" ? `+${t.points_earned}` : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${t.type === "earn" ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-purple-100 text-purple-700 border-purple-200"}`}>
                      {t.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-ink/40">{t.timestamp?.slice(0, 16).replace("T", " ")}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-ink/50">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => { setPage(page - 1); load(page - 1); }} data-testid="prev-page">
              <ChevronLeft size={14} />
            </Button>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => { setPage(page + 1); load(page + 1); }} data-testid="next-page">
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
